import { Cancel, EditOutlined, ErrorOutlineRounded } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Grid,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
    CodeTable,
    fetchAllCodeTablesValues,
    getCodeTableValuesByTableName,
    useCodetablesSlice,
} from "../../../redux/slice/codetableSlice";
import {
    useCommonReducer,
} from "../../../redux/slice/commonSlice";
import {
    practiceProductListByMfr,
    usePracticeProductsSlice,
} from "../../../redux/slice/practice/practiceProductSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
    formatWoundName,
    FieldLabel,
    woundNameWithDimensions,
} from "../../../utils/Utility";
import Button from "../../common/Button";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import OptionsPopup, {
    OptionPopupObjectProps,
} from "../../common/OptionsPopup";
import { useModalStyles } from "../../common/useModalStyles";
import { Package } from "lucide-react";
import { CART_OPTIONS, PRACTICE_TYPE, USAGE_TYPES } from "../../../constants/applicationConstants";
import { fetchManufacturerListByPracticeIds, ManufactureInfo, useManufacturerListSlice } from "../../../redux/slice/manufacturer/ManufacturerSlice";
import { getProductItemkitItem } from "../../../redux/slice/product/createProductSlice";

interface CartOptionAncillaryProductPopupProps {
    open: boolean;
    onClose: () => void;
    editPricing: boolean;
    editProduct: boolean;
    practiceId: number;
    providerId: any;
    woundData: any;
    onAddProduct: (product: AncillaryAddProduct) => void;
    selectedManufactureID: any;
    currentProductInfo?: any;
    practiceSpecialization?: string;
    cartOptionName: string | null;
}
interface AncillaryAddProduct {
    manufacturerId: number;
    selectedProducts: any;
    qty?: number | null;
    woundName: string | null;
    wound?: {
        woundDisplayValue: string | null;
        woundInfoKey: string | null;
    };
    productType?: string[];
    usageType?: String;
    noOfDays?: string | null;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

let erroredFields: string[] = [];

const CartOptionAncillaryProductPopup: React.FC<CartOptionAncillaryProductPopupProps> = ({
    open,
    onClose,
    practiceId,
    editPricing,
    editProduct,
    providerId,
    woundData,
    onAddProduct,
    selectedManufactureID,
    currentProductInfo,
    practiceSpecialization,
    cartOptionName
}) => {
    const showInlineErrorMinLength: number = 60;
    const customTopErrorString: string = "-custom top error";
    const [submitAction, setSubmitAction] = useState<string>();
    const {
        control,
        handleSubmit,
        reset,
        setError,
        watch,
        getValues,
        formState,
        setValue,
    } = useForm<AncillaryAddProduct>();
    const { errors } = formState;
    const [showOptionPopup, setShowOptionsPopup] =
        useState<OptionPopupObjectProps>({ message: "", show: false });
    const dispatch = useAppDispatch();
    const { practiceAncillaryProductsInfo } = usePracticeProductsSlice();
    const { manufacturerListByPractice } = useManufacturerListSlice();
    const { manufacturerInfoListForAncillaryProduct } = useCommonReducer();
    const [shouldAutoSelectProduct, setShouldAutoSelectProduct] = useState(false);

    const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
        useCodetablesSlice();
    const [productData, setProductData] = useState<any>();
    const [productTab, setProductTab] = useState(0);
    const [cartOption, setCartOption] = useState<string | null>(null);
    const [ selectedManufacturer, setSelectedManufacturer ] = useState<ManufactureInfo| null>(null);
    const [ productItemRows, setProductItemRows ] = useState<any[]>([]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        dropdownWrapSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();

    const productOptionsOption2 = useMemo(() => {
        const seen = new Set();
        const options: {
            label: string;
            value: any;
            productItemId: any;
            productId: any;
            practiceProductId: any;
            extendedPrice: any;
        }[] = [];
        practiceAncillaryProductsInfo?.forEach((option: any) => {
            if (!seen.has(option.productItemId)) {
                seen.add(option.productItemId);
                options.push({
                    label: `${option.sku}${option.productItemDescription ? ` - ${option.productItemDescription}` : ""}`,
                    value: option.productItemId,
                    productItemId: option.productItemId,
                    productId: option.productId,
                    practiceProductId: option.practiceProductId,
                    extendedPrice: option.extendedPrice,
                });
            }
        });
        return options;
    }, [practiceAncillaryProductsInfo]);

    const productOptionsOption1 = useMemo(() => {
        const seen = new Set();
        const options: {
            label: string;
            value: any;
            productItemId: any;
            productId: any;
            practiceProductId: any;
            extendedPrice: any;
        }[] = [];
        const selectedCollagenKitDay = getValues("noOfDays");
        practiceAncillaryProductsInfo
            ?.filter((days) => days?.noOfDays === selectedCollagenKitDay)
            ?.forEach((option: any) => {
                if (!seen.has(option.productItemId)) {
                    seen.add(option.productItemId);
                    options.push({
                        label: `${option.sku}${option.productItemDescription ? ` - ${option.productItemDescription}` : ''}`,
                        productItemId: option.productItemId,
                        value: option.productItemId,
                        productId: option.productId,
                        practiceProductId: option.practiceProductId,
                        extendedPrice: option.extendedPrice
                    });
                }
            });
        return options;
    }, [practiceAncillaryProductsInfo, watch('noOfDays')]);

    const errorMessages = useMemo(() => {
        let errorMsgs: JSX.Element[] = [];
        if (errors) {
            let objKeys: string[] = Object.keys(errors);
            let errMsg: string | undefined = undefined;
            if (objKeys && objKeys.length > 0) {
                objKeys.map((key) => {
                    // @ts-ignore
                    errMsg = errors[key].message;
                    if (
                        errMsg &&
                        (errMsg.length >= showInlineErrorMinLength ||
                            errMsg.includes(customTopErrorString))
                    ) {
                        errMsg = errMsg.replace(customTopErrorString, "");
                        errorMsgs.push(
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    color: "error.main",
                                }}
                            >
                                <Box sx={{ pr: 0.5 }}>
                                    <ErrorOutlineRounded />
                                </Box>
                                <Box>{errMsg}</Box>
                            </Box>
                        );
                    }
                });
            }
        }
        return errorMsgs;
    }, [formState]);

    useEffect(() => {
        if (selectedManufactureID) {
            setValue("manufacturerId", selectedManufactureID);
            const data = {
                practiceId: practiceId,
                providerId: providerId,
                dateOfService: null,
                manufacturerId: selectedManufactureID,
                usageType: getValues('usageType') || currentProductInfo?.selectedProducts?.usageType
            };
            dispatch(practiceProductListByMfr(data));
        }
    }, [selectedManufactureID]);

    const woundOptions = useMemo(() => {
        let options: { label: string; value: any }[] = [];

        woundData?.forEach((wound: any) => {
            const length = wound.length ? `${wound.length}cm` : "";
            const width = wound.width ? `${wound.width}cm` : "";
            const depth = wound.depth ? `${wound.depth}cm` : "";
            const woundSize = `${length} x ${width} x ${depth}`;

            options.push({
                label: `${woundNameWithDimensions(
                    wound.woundType,
                    wound.woundInfoKey,
                    wound.length,
                    wound.width,
                    wound.depth
                )}${wound?.procedureLocation ? ` - ${wound?.procedureLocation}` : ""}`,
                value: wound.woundInfoKey,
            });
        });
        setValue('woundName', options[0]?.value);
        return options;
    }, [woundData]);

    const collagenKitDay = useMemo(() => {
        let collagenKitDay: CodeTable[] = [];
        if (allCodeTablesValuesMap) {
            collagenKitDay = getCodeTableValuesByTableName(
                allCodeTablesValuesMap,
                "WoundTypeDay"
            );
        }
        if (!collagenKitDay || collagenKitDay.length === 0) {
            dispatch(fetchAllCodeTablesValues());
        }
        let options: { label: string; value: any }[] = [];
        const selectedWound = woundData?.find(
            (wound: { woundInfoKey: any }) =>
                wound.woundInfoKey === getValues("woundName")
        )?.woundType;
        if (selectedWound) {
            collagenKitDay
                .filter((option) => option.keyName === selectedWound)
                ?.map((option) => {
                    options.push({
                        label: option.displayValue,
                        value: option.displayValue,
                    });
                });
        } else {
            collagenKitDay?.map((option) => {
                options.push({
                    label: option.displayValue,
                    value: option.displayValue,
                });
            });
        }

        return options;
    }, [allCodeTablesValuesMap, watch("woundName"), woundData]);

    useEffect(() => {
        if (currentProductInfo?.manufacturerId) {
            const data = {
                practiceId,
                providerId,
                dateOfService: null,
                manufacturerId: currentProductInfo.manufacturerId,
                usageType: currentProductInfo?.selectedProducts?.usageType
            };
            dispatch(practiceProductListByMfr(data));
            setShouldAutoSelectProduct(true);
        }
        
        if (currentProductInfo) {
            const usageType = currentProductInfo?.selectedProducts?.usageType;
            if (currentProductInfo.cartOption) {
                setCartOption(currentProductInfo.cartOption)
            }
            if (currentProductInfo?.selectedProducts?.productItemId) {
                setProductKitItems(currentProductInfo?.selectedProducts?.productItemId);
            };

            reset({
                usageType: usageType,
                woundName:
                    currentProductInfo?.wound?.woundInfoKey ||
                    currentProductInfo?.woundName ||
                    "",
                manufacturerId: currentProductInfo?.manufacturerId || "",
                noOfDays: currentProductInfo?.noOfDays || "",
                qty: currentProductInfo?.qty || 1
            });
        }
    }, [currentProductInfo]);

    useEffect(() => {
        if (
            shouldAutoSelectProduct &&
            currentProductInfo?.selectedProducts &&
            (productOptionsOption1.length > 0 || productOptionsOption2.length > 0)
        ) {
            const productOptions = cartOption === CART_OPTIONS.OPTION_1 ? productOptionsOption1 : productOptionsOption2
            const selectedProductOption = productOptions.find(
                (opt: any) =>
                    opt.productItemId ===
                    currentProductInfo.selectedProducts?.productItemId ||
                    opt.value === currentProductInfo.selectedProducts?.value
            );
            if (selectedProductOption) {
                setValue("selectedProducts", selectedProductOption.productItemId);
                setProductData(selectedProductOption);
            }
        }
    }, [productOptionsOption1, productOptionsOption2, shouldAutoSelectProduct, cartOption, currentProductInfo]);

    const manufacturersList = useMemo(() => {
        let options: { label: string; value: any }[] = [];

        const activeManufacturers = manufacturerListByPractice?.filter(
            (option: any) => option?.inactiveDate === null
        );

        if (activeManufacturers?.length === 1) {
            setValue("manufacturerId", activeManufacturers[0].manufacturerId);
        }

        activeManufacturers?.forEach((option) => {
            options.push({
                label: option.manufacturerName,
                value: option.manufacturerId,
            });
        });

        return options;
    }, [manufacturerListByPractice]);

    //mount
    useEffect(() => {
        if(practiceId) {
            dispatch(fetchManufacturerListByPracticeIds([practiceId]));
        };
        setValue("productType", ["true", "false"]);

        if (cartOptionName) {
            setCartOption(cartOptionName);
        }
    }, []);

    const onSubmit = useCallback(
        (data: AncillaryAddProduct) => {
            if (productData) {
                const selectedWound = woundData?.find(
                    (wound: { woundInfoKey: any }) =>
                        wound.woundInfoKey === data.woundName
                );
                const newWound = {
                    woundDisplayValue: formatWoundName(
                        selectedWound?.woundType,
                        selectedWound?.woundInfoKey
                    ),
                    woundInfoKey: data.woundName,
                };
                const updatedData = {
                    ...data,

                    qty: 1,
                    cartOption: cartOption,

                    selectedProducts: {
                        ...productData,
                        usageType: cartOption === CART_OPTIONS.OPTION_2 ? getValues('usageType') : null
                    },
                    wound: newWound,
                };
                onAddProduct(updatedData);
            }
            onClose();
        },
        [open, productData]
    );

    const onError = (_errors: any, e: any) => {
        erroredFields.forEach((fieldName: string, index: number) => {
            // @ts-ignore
            setError(fieldName, {});
        });
        erroredFields = [];
    };

    const handleManufacturerChange = async () => {
        const selectedMfrId = getValues("manufacturerId");
        if (selectedMfrId) {
            const data = {
                practiceId: practiceId,
                providerId: providerId,
                dateOfService: null,
                manufacturerId: selectedMfrId,
                usageType: cartOption === CART_OPTIONS.OPTION_2 ? getValues('usageType') : null
            };
            await dispatch(practiceProductListByMfr(data));
        }
    };

    useEffect(() => {
        const selectedMfrId = getValues("manufacturerId");
        const manufacturer = manufacturerListByPractice?.find(mfr => mfr.manufacturerId == selectedMfrId);
        if (selectedMfrId && manufacturer) {
            if(manufacturer) {
                setCartOption(manufacturer.cartOption || CART_OPTIONS.OPTION_1);
                setSelectedManufacturer(selectedManufacturer);
            }
            const data = {
                practiceId: practiceId,
                providerId: providerId,
                dateOfService: null,
                manufacturerId: selectedMfrId,
                usageType: cartOption === CART_OPTIONS.OPTION_2 ? (getValues('usageType') || currentProductInfo?.selectedProducts?.usageType) : null
            };
            if(cartOption) {
            dispatch(practiceProductListByMfr(data));
            };
        }
    }, [watch("manufacturerId"), watch('usageType'), manufacturerListByPractice, cartOption]);

    useEffect(() => {

        if (watch("woundName") === "" || watch("woundName") == null) {
            setValue("selectedProducts", null);
            setProductData(null);
        }
    }, [watch("woundName")]);

    const setUsageType = (tabValue: number) => {
        switch (tabValue) {
            case 0:
                setValue('usageType', USAGE_TYPES.PRIMARY)
                break;
            case 1:
                setValue('usageType', USAGE_TYPES.SECONDARY)
                break;
            case 2:
                setValue('usageType', USAGE_TYPES.ANCILLARY)
                break;
            default:
        }
        handleManufacturerChange();
    };

    const setProductKitItems = async (productItemId: number) => {
        try {
            const response = await dispatch(getProductItemkitItem({ productItemId: productItemId, skipLoader: false }))
            const items = response?.payload?.data;
            if (items && Array.isArray(items)) {
                const data = items.map((item) => ({
                    productName: `${item.productItemDescription} ${item.size}${item.productItemHcpcsCode ? ` (${item.productItemHcpcsCode})` : ""
                        }`,
                    graftQty: item.qty || 0,
                    usageType: item.kitItemUsageType,
                }));
                setProductItemRows(data);
            }
        } catch (error) {
            console.error("Error fetching product item kit data:", error);
        }
    };

    const handleProductChange = async (e: any) => {
        setProductData(e);
        if (e?.productItemId) {
            setProductKitItems(e.productItemId);
        };
    };

    const setLabel = (specialization: string) => {
        switch (specialization) {
            case PRACTICE_TYPE.WOUND_CARE:
                return "Wound";
            case PRACTICE_TYPE.SURGICAL:
                return "Incision";
            case PRACTICE_TYPE.WOUND_CARE_AND_SURGICAL:
                return "Wound/Incision";
            default:
                return "Wound";
        }
    };

    const cartOptions = useMemo(() => {
        let options: { label: string; value: any }[] = [
            { value: "Cart 1", label: "Cart 1" },
            { value: "Cart 2", label: "Cart 2" },
        ];
        return options;
    }, []);

    const tabs = useMemo(() => [...cartOptions.map((s) => s.label)], [cartOptions]);

    return (
        <>
            {showOptionPopup.show && (
                <OptionsPopup
                    open={showOptionPopup.show}
                    onClose={() => setShowOptionsPopup({ message: "", show: false })}
                    variant={showOptionPopup.variant}
                    message={<>{showOptionPopup.message}</>}
                    buttons={showOptionPopup.buttons}
                    title={showOptionPopup.title}
                    obj={showOptionPopup.obj}
                />
            )}
            <Dialog
                open={open}
                TransitionComponent={Transition}
                keepMounted
                onClose={onClose}
                aria-describedby="alert-dialog-slide-description"
                onClick={(event) => {
                    event.stopPropagation();
                }}
                maxWidth="xs"
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        ...paperSx(isMobile),
                        minWidth: {
                            md: 500,
                        },
                    }
                }}
            >
                <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit, onError)}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={headerSx}>
                        <Box sx={iconBoxSx(tokens.accent)}>
                            <Package size={20} color={tokens.accent} strokeWidth={1.8} />
                        </Box>
                        <Box sx={{ flex: 1, pr: 4 }}>
                            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                                Add Product
                            </Typography>
                            <Typography id="alert-dialog-slide-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                                Select manufacturer, wound/incision, and product details.
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>
                    <Box sx={{ ...bodySx, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
                        {errorMessages.length > 0 &&
                            <Box pb={1} ml={0.5}>
                                {errorMessages && errorMessages}
                            </Box>
                        }
                        <Box p={1}>
                            <Grid container spacing={2}>
                                
                                <Grid item xs={12}>
                                    <FieldLabel label="Manufacturer" required tokens={tokens} />
                                    <Box sx={dropdownWrapSx(!!errors.manufacturerId)}>
                                        <CustomDropdownField label="" name="manufacturerId" onChangeSelect={handleManufacturerChange}
                                            options={manufacturersList} control={control} rules={{ required: "Manufacturer is required." }} disabled={selectedManufactureID || manufacturerInfoListForAncillaryProduct?.length === 1 ? true : false} />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <FieldLabel label={`Select ${practiceSpecialization ? setLabel(practiceSpecialization) : "Wound"}`} required tokens={tokens} />
                                    <Box sx={dropdownWrapSx(!!errors.woundName)}>
                                        <CustomDropdownField
                                            label=""
                                            name="woundName"
                                            options={woundOptions}
                                            control={control}
                                            showErrorMinLength={showInlineErrorMinLength}
                                            rules={{
                                                required: `${practiceSpecialization ? setLabel(practiceSpecialization) : "Wound"} is required.`,
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                {watch("manufacturerId") &&
                                    manufacturersList.find(
                                        (mfr) =>
                                            mfr.value === watch("manufacturerId") &&
                                            cartOption === CART_OPTIONS.OPTION_2
                                    ) && (
                                        <Grid item xs={12}>
                                            <FieldLabel label="Usage Type" required tokens={tokens} />
                                            <Box sx={dropdownWrapSx(!!errors.usageType)}>
                                                <CustomDropdownField
                                                    label=""
                                                    name="usageType"
                                                    options={[
                                                        { label: "Primary", value: USAGE_TYPES.PRIMARY },
                                                        { label: "Secondary", value: USAGE_TYPES.SECONDARY },
                                                        { label: "Ancillary", value: USAGE_TYPES.ANCILLARY },
                                                    ]}
                                                    control={control}
                                                    rules={{ required: "Usage Type is required." }}
                                                    onChangeSelect={(selected) => {
                                                        const usageType = selected?.value;
                                                        setValue("usageType", usageType);
                                                        // handleManufacturerChange();
                                                    }}
                                                    disabled={!!currentProductInfo}
                                                />
                                            </Box>
                                        </Grid>
                                    )}
                                { cartOption === CART_OPTIONS.OPTION_1 &&
                                    <Grid item xs={12}>
                                    <FieldLabel label="Daily Supply" required tokens={tokens} />
                                    <Box sx={dropdownWrapSx(!!errors.noOfDays)}>
                                        <CustomDropdownField label="" name="noOfDays"
                                            options={collagenKitDay} control={control}
                                            rules={{ required: "Daily Supply is required." }}
                                            disabled={getValues("woundName") ? false : true}
                                        />
                                    </Box>
                                </Grid>}
                                {getValues("manufacturerId") && (<>
                                    <Grid item xs={12}>
                                        <FieldLabel label="Product" required tokens={tokens} />
                                        <Box sx={dropdownWrapSx(!!errors.selectedProducts)}>
                                            <CustomDropdownField
                                                name={`selectedProducts`}
                                                control={control}
                                                onChangeSelect={(e) => { handleProductChange(e) }}
                                                options={ cartOption === CART_OPTIONS.OPTION_2 && getValues('usageType') ? productOptionsOption2 : cartOption === CART_OPTIONS.OPTION_1 ? productOptionsOption1 : []}
                                                label=""
                                                rules={
                                                    getValues("manufacturerId") ? { required: "Select the products" } : {}
                                                }
                                                disabled={ cartOption === CART_OPTIONS.OPTION_1 && !watch('noOfDays') ? true : false }
                                            />
                                        </Box>
                                    </Grid>
                                </>)}
                                {
                                    productItemRows?.length > 0 && <Grid item xs={12} >
                                        {productItemRows.map((item, index) => (
                                            <Typography key={index} sx={{ pt: 0, ml: 2 }}>
                                                • {item.productName} - Qty: {item.graftQty}
                                            </Typography>
                                        ))}
                                    </Grid>
                                }
                            </Grid>
                        </Box>
                    </Box>
                    <Box sx={footerSx}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1.5,
                                flexDirection: { xs: "column", sm: "row" },
                                width: "100%",
                            }}
                        >
                            <Box
                                component="button"
                                type="submit"
                                onClick={() => {
                                    if (editPricing) {
                                        setSubmitAction("UpdateProductPrice")
                                    } else {
                                        setSubmitAction("AddingNewProductPricing")
                                    }
                                }}
                                sx={{
                                    ...primaryBtnSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    px: 3,
                                    py: 1.2,
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#fff",
                                    order: { xs: 1, sm: 2 },
                                }}
                            >
                                <EditOutlined sx={{ fontSize: "1.1rem" }} />
                                Add Product
                            </Box>
                            <Box
                                component="button"
                                type="button"
                                onClick={onClose}
                                sx={{
                                    ...cancelBtnSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    px: 3,
                                    py: 1.2,
                                    border: "1px solid",
                                    cursor: "pointer",
                                    order: { xs: 2, sm: 1 },
                                }}
                            >
                                <Cancel sx={{ fontSize: "1.1rem" }} />
                                Cancel
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default CartOptionAncillaryProductPopup;
